/**
 * Sidebar navigation — จัดกลุ่มตามหมวดการทำงาน (อิง docs/specs A1–A8)
 */
import {
  ArrowLeftRight,
  Banknote,
  BarChart3,
  Car,
  CirclePlus,
  Coins,
  FileCheck,
  FileQuestion,
  History,
  LayoutDashboard,
  LayoutGrid,
  Package,
  Percent,
  RotateCcw,
  Scale,
  Send,
  ShieldAlert,
  ShieldCheck,
  Store,
  Tag,
  UserCog,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { PartzyncLogo } from "../PartzyncLogo";
import type { SidebarData } from "../types";

export const sidebarData: SidebarData = {
  teams: [
    {
      name: "PARTZYNC Admin",
      logo: PartzyncLogo,
      plan: "Admin Panel",
    },
  ],
  navGroups: [
    {
      title: "ທົ່ວໄປ",
      items: [
        {
          title: "ແຜງຄວບຄຸມ",
          url: "/app/dashboard",
          icon: LayoutDashboard,
        },
      ],
    },
    {
      title: "ຮ້ານຄ້າ & ຊ່າງແປງ",
      items: [
        {
          title: "ຮ້ານຄ້າ",
          url: "/app/shops",
          icon: Store,
          requiredPermissions: ["shops:list"],
        },
        {
          title: "ລາຍງານການປະຕິບັດງານ",
          url: "/app/shops/performance",
          icon: BarChart3,
          requiredPermissions: ["shops:read", "reports:read"],
        },
        {
          title: "ຄິວລົງທະບຽນຊ່າງ",
          url: "/app/mechanics",
          icon: Wrench,
          requiredPermissions: ["mechanics:list"],
        },
      ],
    },
    {
      title: "Catalog & ຫມວດຫມູ່",
      items: [
        {
          title: "ຫມວດຫມູ່",
          url: "/app/categories",
          icon: LayoutGrid,
          requiredPermissions: ["categories:list"],
        },
        {
          title: "ສິນຄ້າ (Catalog)",
          url: "/app/catalog",
          icon: Package,
          requiredPermissions: ["catalog:read"],
        },
        {
          title: "ຍີ່ຫໍ້ອາໄຫຼ່",
          url: "/app/catalog/brands",
          icon: Tag,
          requiredPermissions: ["catalog:read"],
        },
        {
          title: "ຂໍ້ມູນລົດ (Vehicle Data)",
          url: "/app/catalog/vehicles",
          icon: Car,
          requiredPermissions: ["catalog:read"],
        },
        {
          title: "ຄິວ fitment / POS provision",
          url: "/app/catalog/pending-fitment",
          icon: FileQuestion,
          requiredPermissions: ["catalog:read"],
        },
        {
          title: "ຄິວ catalog candidate",
          url: "/app/catalog/candidates",
          icon: FileQuestion,
          requiredPermissions: ["catalog:read"],
        },
      ],
    },
    {
      title: "ຄ່າທຳນຽມ & ການກວດກາ",
      items: [
        {
          title: "ຄ່າທຳນຽມ",
          url: "/app/fees",
          icon: Coins,
          requiredPermissions: ["fee_rules:list"],
        },
        {
          title: "ການກວດກາ Marketplace",
          url: "/app/moderation",
          icon: ShieldAlert,
          requiredPermissions: ["listings:list"],
        },
        {
          title: "ລາຍງານ",
          url: "/app/moderation/reports",
          icon: ShieldCheck,
          requiredPermissions: ["reports:list"],
        },
        {
          title: "ປະຫວັດການກວດກາ",
          url: "/app/moderation/history",
          icon: History,
          requiredPermissions: ["moderation:history"],
        },
      ],
    },
    {
      title: "ຂໍ້ພິພາດ",
      items: [
        {
          title: "ຂໍ້ພິພາດ",
          url: "/app/disputes",
          icon: Scale,
          requiredPermissions: ["disputes:list"],
        },
      ],
    },
    {
      title: "ກະເປົາເງິນ & ການຈ່າຍເງິນ",
      items: [
        {
          title: "ພາບລວມກະເປົາເງິນ",
          url: "/app/wallet",
          icon: Banknote,
          requiredPermissions: ["wallet:overview"],
        },
        {
          title: "ບັນຊີກະເປົາເງິນ",
          url: "/app/wallet/accounts",
          icon: Wallet,
          requiredPermissions: ["wallet:list"],
        },
        {
          title: "ລາຍການທຸລະກຳ",
          url: "/app/wallet/transactions",
          icon: ArrowLeftRight,
          requiredPermissions: ["wallet:transactions"],
        },
        {
          title: "ຄຳຂໍເຕີມເງິນ",
          url: "/app/wallet/topup",
          icon: CirclePlus,
          requiredPermissions: ["top_up:list"],
        },
        {
          title: "ຄຳຂໍ KYC",
          url: "/app/wallet/kyc-requests",
          icon: ShieldCheck,
          requiredPermissions: ["compliance:read"],
        },
        {
          title: "ຄືນເງິນ",
          url: "/app/wallet/refunds",
          icon: RotateCcw,
          requiredPermissions: ["refund:list"],
        },
        {
          title: "ການຈ່າຍເງິນອອກ",
          url: "/app/wallet/payouts",
          icon: Send,
          requiredPermissions: ["payout:list"],
        },
        {
          title: "ຄ່າທຳນຽມກະເປົາ",
          url: "/app/wallet/fees",
          icon: Percent,
          requiredPermissions: ["wallet_fee:list"],
        },
        {
          title: "ຄວາມສອດຄ່ອງ & ຄວາມສ່ຽງ",
          url: "/app/wallet/compliance",
          icon: ShieldAlert,
          requiredPermissions: ["compliance:read"],
        },
        {
          title: "ການຕິດຕາມກັນ",
          url: "/app/wallet/reconciliation",
          icon: FileCheck,
          requiredPermissions: ["reconciliation:list"],
        },
      ],
    },
    {
      title: "ລະບົບ & ສິດການເຂົ້າເຖິງ",
      items: [
        {
          title: "ບົດບາດ",
          url: "/app/roles",
          icon: UserCog,
          requiredPermissions: ["roles:read"],
        },
        {
          title: "ຜູ້ໃຊ້",
          url: "/app/users",
          icon: Users,
          requiredPermissions: ["users:read"],
        },
        {
          title: "ບັນທຶກການກວດກາ",
          url: "/app/audit",
          icon: ShieldCheck,
          requiredPermissions: ["audit:read"],
        },
      ],
    },
  ],
};
